import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  type ChatSessionDerivationRow,
  SESSION_MESSAGE_EMBED,
  SESSION_MESSAGE_WINDOW,
  derivePreview,
  deriveTitle,
  withChronologicalMessages,
} from './chat-session-derivation';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';

/**
 * Conversations attached to a single project (campaign).
 * Reads `ai_sessions WHERE project_id = X` ordered by `updated_at DESC`.
 *
 * Click → /?planId=<projectId>&resumeSession=<sessionId> (home chat with
 * project context pre-loaded AND session resumed).
 */

export interface ProjectConversation {
  id: string;
  workspaceId: string;
  title: string;
  preview: string;
  updatedAt: string;
  status: string;
}

export interface UseProjectConversationsOptions {
  /** How many rows to fetch. Strip preview shows 2; full sheet shows more. */
  limit?: number;
}

export interface UseProjectConversationsResult {
  conversations: ProjectConversation[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
}

export function useProjectConversations(
  projectId: string | null | undefined,
  options: UseProjectConversationsOptions = {},
): UseProjectConversationsResult {
  const { limit = 5 } = options;
  const { session } = useAuth();
  const userId = session?.user?.id;
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;

  const query = useQuery({
    queryKey: ['project-conversations', projectId, userId, activeKitId, limit] as const,
    enabled: !!userId && !!projectId && !!activeKitId && !isActiveKitPending,
    staleTime: 15_000,
    queryFn: async (): Promise<{ rows: ProjectConversation[]; total: number }> => {
      if (!userId || !projectId || !activeKitId) return { rows: [], total: 0 };

      const { data, error, count } = await supabase
        .from('ai_sessions')
        .select(
          `id, brand_kit_id, title, status, updated_at,
           ai_messages (role, content, parts, created_at)`,
          { count: 'exact' },
        )
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .eq('brand_kit_id', activeKitId)
        .neq('status', 'archived')
        .order('updated_at', { ascending: false })
        .order('created_at', { ...SESSION_MESSAGE_EMBED, ascending: false })
        .limit(SESSION_MESSAGE_WINDOW, SESSION_MESSAGE_EMBED)
        .limit(limit);

      if (error) {
        console.error('[useProjectConversations]', error);
        return { rows: [], total: 0 };
      }

      const rows = withChronologicalMessages((data ?? []) as unknown as ChatSessionDerivationRow[]);
      const mapped = rows
        .filter((row) => (row.ai_messages?.length ?? 0) > 0)
        .map((row) => ({
          id: row.id,
          workspaceId: row.brand_kit_id ?? activeKitId,
          title: deriveTitle(row),
          preview: derivePreview(row),
          updatedAt: row.updated_at,
          status: row.status,
        }));

      return { rows: mapped, total: count ?? mapped.length };
    },
  });

  return {
    conversations: query.data?.rows ?? [],
    totalCount: query.data?.total ?? 0,
    isLoading: isActiveKitPending || query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

export function useInvalidateProjectConversations() {
  const qc = useQueryClient();
  return useCallback(
    (projectId: string | null | undefined) => {
      qc.invalidateQueries({ queryKey: ['project-conversations', projectId] });
    },
    [qc],
  );
}
